import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { 
  DollarSign, 
  Users, 
  Target, 
  CheckCircle2, 
  TrendingUp, 
  Calendar,
  Shield,
  Zap,
  MessageSquare,
  ExternalLink,
  Phone,
  Search,
  Award,
  HelpCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Affiliates = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Affiliate Program | ShadowScan Partner Resources</title>
        <meta
          name="description"
          content="Join the ShadowScan Affiliate Program. Earn commission on eligible paid ShadowScan plans and access sales resources for hosted and monitored WordPress security offers."
        />
      </Helmet>
      <Navigation />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/5 [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]" />
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4 px-4 py-1 text-primary border-primary/30">
                AFFILIATE PARTNER RESOURCES
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                ShadowScan Affiliate Program
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
                Earn <span className="text-primary font-semibold">75% commission</span> on every active domain - real commissions for real, paying customers. 
                No leads, no sign-ups - only activated services count.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg" className="gap-2">
                  <a href="https://shadowscan.getrewardful.com/signup" target="_blank" rel="noopener noreferrer">
                    <Award className="h-5 w-5" />
                    Become an Affiliate
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="gap-2">
                  <a href="https://findleads.lovable.app" target="_blank" rel="noopener noreferrer">
                    <Users className="h-5 w-5" />
                    Open Lead Finder
                  </a>
                </Button>
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
              <Card className="bg-card/50 backdrop-blur border-primary/20">
                <CardContent className="p-6 text-center">
                  <DollarSign className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <div className="text-3xl font-bold text-primary">75%</div>
                  <div className="text-sm text-muted-foreground">Commission Rate</div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur border-primary/20">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <div className="text-3xl font-bold text-green-500">50%</div>
                  <div className="text-sm text-muted-foreground">Discount vs Direct</div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur border-primary/20">
                <CardContent className="p-6 text-center">
                  <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <div className="text-3xl font-bold text-blue-500">Monthly</div>
                  <div className="text-sm text-muted-foreground">Payouts</div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 backdrop-blur border-primary/20">
                <CardContent className="p-6 text-center">
                  <Award className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                  <div className="text-3xl font-bold text-amber-500">$200</div>
                  <div className="text-sm text-muted-foreground">Min Payout</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>


        {/* Sales Tips Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Sales Tips & Strategies</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Use these proven strategies to convert leads into active customers
              </p>
            </div>

            {/* Lead Generator CTA */}
            <Card className="mb-8 bg-gradient-to-r from-primary/10 via-primary/5 to-accent/10 border-primary/30">
              <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-full bg-primary/20">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Free Lead Generator Tool</h3>
                    <p className="text-muted-foreground">
                      Find WordPress websites in any niche - build your prospect list in seconds
                    </p>
                  </div>
                </div>
                <Button asChild size="lg" className="gap-2 shrink-0">
                  <a href="https://findleads.lovable.app" target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-5 w-5" />
                    Open Lead Finder
                  </a>
                </Button>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Search className="h-5 w-5 text-primary" />
                    Use the Lead Finder Tool
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">
                    Use our free lead generator to find WordPress websites in any niche. Build a targeted prospect list 
                    in seconds - then reach out with your affiliate offer.
                  </p>
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <a href="https://findleads.lovable.app" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Open Lead Finder
                    </a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Phone className="h-5 w-5 text-primary" />
                    Call with Results Ready
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Complete the scan before calling. Open with: "I ran a quick security check on your site and found 
                    [X vulnerabilities]. I'd love to show you how we can fix this for just $25/month."
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="h-5 w-5 text-primary" />
                    Target Multi-Site Clients
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Agencies, developers, and businesses with multiple sites are ideal prospects. 
                    More domains = more commission. A client with 10 sites at $25 each earns you $187.50.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Lead with Value
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Emphasize the $25 affiliate rate vs $50 direct sign-up. "I can get you 50% off through my 
                    partnership with ShadowScan - that's monitored security with validated guidance for just $25/month."
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Shield className="h-5 w-5 text-primary" />
                    Highlight Real Humans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Our Premium plan includes analyst-led review and response support, not just automated tooling.
                    This is a key differentiator: real people validate higher-risk events and guide next steps.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-primary/20 hover:border-primary/40 transition-colors">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Zap className="h-5 w-5 text-primary" />
                    Managed Security
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    ShadowScan spans free ShadowScan Guard, Basic Hosting, and monitored service levels.
                    "For paid monitored plans, ShadowScan validates suspicious activity and supports response within the agreed scope."
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">How the Program Works</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Simple, transparent, and fair - you earn when clients activate and pay
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">1</div>
                  <div>
                    <h3 className="font-semibold mb-1">Refer Using Your Affiliate Link</h3>
                    <p className="text-muted-foreground">Share your unique affiliate link with prospects. All sign-ups through your link are tracked automatically.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">2</div>
                  <div>
                    <h3 className="font-semibold mb-1">Client Creates Account</h3>
                    <p className="text-muted-foreground">Your prospect signs up for a ShadowScan account using your referral link.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">3</div>
                  <div>
                    <h3 className="font-semibold mb-1">They Add Domains</h3>
                    <p className="text-muted-foreground">The client adds one or more domains/websites to their account for monitoring.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">4</div>
                  <div>
                    <h3 className="font-semibold mb-1">Service Activation & Payment</h3>
                    <p className="text-muted-foreground">Once the security service is activated and the upfront pro-rata payment is made, the sale counts.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">5</div>
                  <div>
                    <h3 className="font-semibold mb-1">You Earn Commission</h3>
                    <p className="text-muted-foreground">75% of the first sale per active domain - a one-time commission when the service is activated.</p>
                  </div>
                </div>
              </div>

              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                    What Counts as a Sale
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    A sale is counted <strong>only when all</strong> of the following are true:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Client has created a ShadowScan account</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>At least one domain has been added</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Security service is activated</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>Upfront pro-rata payment has been successfully made</span>
                    </li>
                  </ul>
                  <div className="pt-4 border-t border-border">
                    <p className="text-sm font-medium text-amber-600 dark:text-amber-400">
                      ⚠️ No activation = no commission. Simple.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Pricing Guide for Affiliates</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Share these prices with your prospects. This affiliate page focuses on eligible paid plans, not the free ShadowScan Guard layer.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Essential Plan */}
              <Card className="border-2 border-secondary/50">
                <CardHeader className="bg-secondary/10 border-b border-secondary/20">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">Essential</CardTitle>
                    <Badge variant="secondary">Monitored Security</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <p className="text-muted-foreground">
                    Strong automatic protection with validated guidance for monitored WordPress security.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Detects repeated login abuse and contains it safely
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Monitors integrity changes and admin activity
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Reduces exposed entry points like XML-RPC and enumeration
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Prioritises vulnerabilities by exploitability and exposure
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Early visibility before public disclosure where possible
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Validates high-risk alerts to reduce noise
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Guides safe core, plugin, and theme updates
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      File integrity monitoring for unauthorised changes
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Clear remediation steps and runbooks
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      Application-level protections with monitored guidance
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      No setup fees or routine labour charges
                    </li>
                  </ul>
                  <div className="pt-4 border-t">
                    <div className="text-2xl font-bold text-primary">
                      <span className="line-through text-muted-foreground text-lg mr-2">$50</span>
                      $25/month
                    </div>
                    <p className="text-xs text-emerald-500 font-medium">Affiliate exclusive pricing (1-999 sites)</p>
                  </div>
                </CardContent>
              </Card>

              {/* Premium Plan */}
              <Card className="border-2 border-primary">
                <CardHeader className="bg-primary/10 border-b border-primary/20">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-2xl">Premium</CardTitle>
                    <Badge>SOC Operations and Incident Ownership</Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <p className="text-muted-foreground">
                    Everything in Essential plus SOC operations and incident ownership.
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Includes everything in Essential
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Active monitoring by security analysts
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Incident ownership with containment and stabilization
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Remediation performed by ShadowScan
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Continuous tuning of thresholds and controls
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Post-incident review and hardening
                    </li>
                    <li className="flex items-center gap-2 text-primary font-medium">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      Dedicated account manager (for Premium portfolios)
                    </li>
                  </ul>
                  <div className="pt-4 border-t">
                    <div className="text-2xl font-bold text-primary">$185/month</div>
                    <p className="text-xs text-muted-foreground">Standard pricing (1-999 sites)</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Volume Pricing Table */}
            <Card>
              <CardHeader>
                <CardTitle>Volume Pricing (Per Website/Domain)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">Website Volume</th>
                        <th className="text-center py-3 px-4 font-semibold">Essential Plan</th>
                        <th className="text-center py-3 px-4 font-semibold">Premium Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-3 px-4">1 - 999 sites</td>
                        <td className="py-3 px-4 text-center"><span className="line-through text-muted-foreground mr-1">$50</span> <span className="text-primary font-semibold">$25</span></td>
                        <td className="py-3 px-4 text-center">$185 AUD</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4">1,000+ sites</td>
                        <td className="py-3 px-4 text-center">$40 AUD</td>
                        <td className="py-3 px-4 text-center">$90 AUD</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4">2,000+ sites</td>
                        <td className="py-3 px-4 text-center">$30 AUD</td>
                        <td className="py-3 px-4 text-center">$85 AUD</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-3 px-4">5,000+ sites</td>
                        <td className="py-3 px-4 text-center">$25 AUD</td>
                        <td className="py-3 px-4 text-center">$80 AUD</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4">10,000+ sites</td>
                        <td className="py-3 px-4 text-center">$10 AUD</td>
                        <td className="py-3 px-4 text-center">$70 AUD</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  * Affiliate $25 pricing applies to Essential Plan for 1-999 sites. Direct sign-up is $50.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Your Earnings Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Your Earnings</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Earn per active website, not per client - more domains mean more commission
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="text-center bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                <CardContent className="pt-8 pb-6">
                  <div className="text-5xl font-bold text-green-500 mb-2">75%</div>
                  <div className="text-lg font-semibold mb-2">One-Time Commission</div>
                  <p className="text-sm text-muted-foreground">
                    Earn 75% of the first payment for each domain activated
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                <CardContent className="pt-8 pb-6">
                  <div className="text-5xl font-bold text-blue-500 mb-2">Monthly</div>
                  <div className="text-lg font-semibold mb-2">Payout Schedule</div>
                  <p className="text-sm text-muted-foreground">
                    Commissions paid monthly for sales made in the previous period
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                <CardContent className="pt-8 pb-6">
                  <div className="text-5xl font-bold text-amber-500 mb-2">$200</div>
                  <div className="text-lg font-semibold mb-2">Minimum Payout</div>
                  <p className="text-sm text-muted-foreground">
                    Reach $200 in commissions to receive your payout
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8 p-6 bg-muted/50 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Example Earnings
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 bg-background rounded border">
                  <div className="font-semibold">Small Business</div>
                  <div className="text-muted-foreground">1 domain @ $25 × 75% = <span className="text-primary font-bold">$18.75</span></div>
                </div>
                <div className="p-4 bg-background rounded border">
                  <div className="font-semibold">Small Agency</div>
                  <div className="text-muted-foreground">10 domains @ $25 × 75% = <span className="text-primary font-bold">$187.50</span></div>
                </div>
                <div className="p-4 bg-background rounded border">
                  <div className="font-semibold">Large Agency</div>
                  <div className="text-muted-foreground">50 domains @ $25 × 75% = <span className="text-primary font-bold">$937.50</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-4xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 flex items-center justify-center gap-2">
                <HelpCircle className="h-8 w-8 text-primary" />
                Affiliate FAQ
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  Do you offer domain security or website security?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  We provide <strong>website security</strong>. Domains are used to identify sites, but the protection 
                  is applied to the website itself - including WordPress core, plugins, themes, and all associated files.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  When does a referral officially count as a sale?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Only when the service is <strong>activated and paid for</strong>. Sign-ups alone don't count. 
                  The client must complete the onboarding, add a domain, activate service, and make their 
                  first payment.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  Do I get paid if the client cancels before the month starts?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  No. The service must be <strong>active and billed</strong> to qualify. If a client cancels 
                  before their billing cycle begins, no commission is earned for that period.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  What happens if a client adds more domains later?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  You earn commission on every additional active domain they secure <strong>within 30 days</strong> of 
                  their initial sign-up through your affiliate link.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  How do clients pay?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Clients pay an <strong>upfront pro-rata amount</strong> when they activate, then ongoing billing 
                  occurs on the <strong>1st of each month</strong>. Pro-rata ensures they're only charged for the 
                  remaining days in the current month.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  What's the difference between affiliate and direct pricing?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Direct sign-up via the website is <strong>$50/month</strong> for Essential. Affiliates can offer 
                  their leads a discounted rate of <strong>$25/month</strong> through their affiliate link - 
                  that's a 50% discount for your prospects.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  When exactly do affiliates get paid?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Affiliate payouts are processed <strong>monthly</strong>, based on new sales made in 
                  the previous period. Commissions are one-time per domain activation.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  Is there a minimum payout threshold?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  Yes - <strong>$200 minimum</strong>. Once your commissions reach $200, you'll receive your payout 
                  in the next payment cycle.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9" className="bg-card rounded-lg border px-6">
                <AccordionTrigger className="hover:no-underline">
                  Can I promote ShadowScan to agencies or developers?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">
                  <strong>Yes!</strong> Agencies, developers, and businesses with multiple sites are ideal targets. 
                  More domains mean more commission. A single agency client with 20 sites earns you $375 (75% of $500).
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Start Earning?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Sign up as an affiliate, use our free lead finder to build your prospect list, then convert them with 
              your exclusive $25 affiliate pricing.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="gap-2">
                <a href="https://shadowscan.getrewardful.com/signup" target="_blank" rel="noopener noreferrer">
                  <Award className="h-5 w-5" />
                  Become an Affiliate
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="gap-2">
                <a href="https://findleads.lovable.app" target="_blank" rel="noopener noreferrer">
                  <Users className="h-5 w-5" />
                  Open Lead Finder
                </a>
              </Button>
            </div>
            <div className="mt-8 text-sm text-muted-foreground">
              <p>Need help? Email us at <a href="mailto:bbridle@shadowscan.com.au" className="text-primary hover:underline">bbridle@shadowscan.com.au</a></p>
              <p>Visit <a href="https://shadowscan.com.au" className="text-primary hover:underline">shadowscan.com.au</a></p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Affiliates;
